/**
 * The date control for `scheduledPublishAt`.
 *
 * `functions/src/scheduledPublish.ts` has been complete and tested for a while
 * and could only be reached from the Firestore console, because nothing in the
 * CMS wrote the field. This is that control.
 *
 * It is `IsoDateField` and not FireCMS's `date` property, for the reason
 * `IsoDateField`'s own header gives and which bites hardest here: a Firestore
 * `Timestamp` is invisible to the publish gate, and the scheduler's own query
 * orders by type before value, so a Timestamp and an ISO string are two
 * different worlds. `readDue` asks both questions to survive exactly this, but
 * writing the type the content model asks for is the fix rather than the
 * workaround.
 *
 * The rest of this component is one sentence that has to be on screen:
 * **scheduling is not a promise to publish.** At the scheduled moment the guide
 * goes through the full gate, and a guide that fails it is put back to
 * `approved` with the reasons — which is the right outcome, and a surprise to
 * anyone who read "schedule" as "publish later, definitely".
 */

import React from "react";
import { FieldProps } from "@firecms/core";
import { Alert, Typography } from "@firecms/ui";
import type { GuideStatus } from "@zennopay/schema";

import { IsoDateField, IsoDateFieldProps } from "./IsoDateField";

/** Where a guide has to be for the scheduler to act on it. */
const PROMOTES_FROM: GuideStatus = "approved";

export function ScheduledPublishField(props: FieldProps<string, IsoDateFieldProps>) {
    const { value, context } = props;
    const status = (context.values as { status?: GuideStatus }).status;
    const scheduled = Boolean(value && !Number.isNaN(Date.parse(value)));
    const inThePast = scheduled && Date.parse(value) < Date.now();

    return (
        <div className={"flex flex-col gap-2"}>
            <IsoDateField {...props}/>

            {!scheduled && (
                <Typography variant={"caption"} className={"opacity-70"}>
                    Leave this empty to publish by hand. Set it and{" "}
                    <code>publishScheduled</code> promotes the guide within five minutes of
                    that moment — but only if the guide is <strong>approved</strong> by then,
                    and only if it passes the publish gate at that moment.
                </Typography>
            )}

            {scheduled && (
                <Alert color={inThePast ? "warning" : "info"} size={"small"}>
                    <div className={"flex flex-col gap-1"}>
                        <Typography variant={"body2"}>
                            <strong>Scheduling does not guarantee publication.</strong> At the
                            scheduled moment this guide faces the full publish gate, exactly as
                            it would if somebody pressed publish. If it fails — a claim has
                            expired, or a link now points at a page the site no longer serves —
                            nothing is published, the status goes back to approved, and the
                            reason appears in the publish state at the top of this form.
                        </Typography>
                        {status !== PROMOTES_FROM && (
                            <Typography variant={"body2"}>
                                This guide is <strong>{status ?? "draft"}</strong>, not approved.
                                The scheduler only promotes approved guides; if it is still
                                {" "}{status ?? "draft"} when the time comes, the schedule is
                                cleared and nothing is published.
                            </Typography>
                        )}
                        {inThePast && (
                            <Typography variant={"body2"}>
                                This time has already passed. The next scheduled run will act on
                                it, so treat it as "as soon as possible" rather than as history.
                            </Typography>
                        )}
                        <Typography variant={"caption"} className={"opacity-80"}>
                            Note who can set this: <code>firestore.rules</code> lets an editor
                            write to a guide only while it is a draft or in review, and lets a
                            publisher write only in the same save that changes its status. So
                            schedule before approval, or ask an admin.
                        </Typography>
                    </div>
                </Alert>
            )}
        </div>
    );
}
